"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Scale, ShieldCheck } from "lucide-react";
import { useTranslations } from "next-intl";
import { carDetailPath } from "@/lib/car-slug";
import { compareQueryFromIds, readCompareCarIds, toggleCompareCarId } from "@/lib/compare-cars";
import { cn } from "@/lib/utils";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import type { ComparePickerCar } from "@/components/compare/compare-picker-types";

export default function CompareCarPickerGrid({ cars }: { cars: ComparePickerCar[] }) {
  const tPick = useTranslations("ComparePage");
  const tDetail = useTranslations("CarDetailPage");
  /** carId → in compare tray */
  const [inSet, setInSet] = useState<Record<string, boolean>>({});
  const [counts, setCounts] = useState(0);

  useEffect(() => {
    const sync = () => {
      const list = readCompareCarIds();
      setCounts(list.length);
      const next: Record<string, boolean> = {};
      for (const id of list) next[id] = true;
      setInSet(next);
    };
    const tmr = window.setTimeout(sync, 0);
    window.addEventListener("beepbeep-compare-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.clearTimeout(tmr);
      window.removeEventListener("beepbeep-compare-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  function onToggle(carId: string) {
    const { action } = toggleCompareCarId(carId);
    if (action === "limit") {
      window.alert(tDetail("compareLimitMsg"));
      return;
    }
    const list = readCompareCarIds();
    setCounts(list.length);
    const next: Record<string, boolean> = {};
    for (const id of list) next[id] = true;
    setInSet(next);
  }

  if (cars.length < 1) {
    return null;
  }

  const hrefWhenReady =
    counts >= 2 ? `/cars/compare?ids=${compareQueryFromIds(readCompareCarIds())}` : null;

  return (
    <section className="mt-14 border-t border-gray-100 pt-12 dark:border-gray-800" aria-labelledby="compare-picker-heading">
      <div className="mx-auto max-w-2xl text-center">
        <h2 id="compare-picker-heading" className="text-lg font-semibold text-gray-900 dark:text-gray-100 md:text-xl">
          {tPick("pickSectionTitle")}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{tPick("pickSectionSubtitle")}</p>
      </div>

      {hrefWhenReady ? (
        <div className="mx-auto mt-6 flex justify-center">
          <Link href={hrefWhenReady}>
            <Button size="sm" className="bg-[#E63946] hover:bg-[#c1121f]">
              {tPick("pickOpenCompare")}
            </Button>
          </Link>
        </div>
      ) : null}

      <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cars.map((c, index) => {
          const inCompare = !!inSet[c.id];
          const path = carDetailPath(c);
          const tier = String(c.listing_tier ?? "standard").trim().toLowerCase();
          const tierRing =
            tier === "premium"
              ? "border-purple-200 ring-1 ring-purple-100 dark:border-purple-800 dark:ring-purple-900/40"
              : tier === "top"
                ? "border-amber-200 dark:border-amber-800"
                : "border-gray-100 dark:border-gray-800";

          return (
            <article
              key={c.id}
              className={cn(
                "flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm dark:bg-gray-900/80",
                tierRing,
                inCompare && "ring-2 ring-[#E63946]/50",
              )}
            >
              <Link href={path} className="relative block h-44 shrink-0 bg-gray-100 dark:bg-gray-800">
                {c.imageUrl ? (
                  <Image
                    src={c.imageUrl}
                    alt={c.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 380px"
                    className="object-cover transition-transform hover:scale-[1.03]"
                    preload={index < 3}
                    loading={index < 3 ? "eager" : undefined}
                    fetchPriority={index < 3 ? (index === 0 ? "high" : "auto") : undefined}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-300 dark:text-gray-600">
                    <Scale size={40} strokeWidth={1} aria-hidden />
                  </div>
                )}
                {!c.is_available && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <span className="text-sm font-medium text-white">{tPick("pickerUnavailableOverlay")}</span>
                  </div>
                )}
                {c.is_listing_verified ? (
                  <div className="absolute left-2 top-2">
                    <Badge variant="success" className="gap-0.5 text-[10px] shadow-sm">
                      <ShieldCheck size={10} aria-hidden />
                      {tPick("verified")}
                    </Badge>
                  </div>
                ) : null}
              </Link>
              <div className="flex flex-1 flex-col gap-3 p-4">
                <div>
                  <Link href={path} className="line-clamp-2 text-base font-semibold text-gray-900 hover:text-[#E63946] dark:text-gray-100">
                    {c.brand} {c.model}
                  </Link>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {c.year}
                    {" · "}
                    {Math.round(Number(c.price_per_day))} {c.currency}
                    {tDetail("perDaySlash")}
                  </div>
                </div>
                <Button
                  type="button"
                  variant={inCompare ? "outline" : "outline"}
                  size="sm"
                  className={cn(
                    "mt-auto w-full gap-2 border-gray-300 font-semibold",
                    inCompare && "border-[#E63946]/40 bg-[#E63946]/5 text-[#E63946] dark:border-[#E63946]/50 dark:bg-[#E63946]/15",
                  )}
                  onClick={() => onToggle(c.id)}
                  aria-pressed={inCompare}
                >
                  <Scale size={16} strokeWidth={2} aria-hidden />
                  {inCompare ? tDetail("compareRemove") : tDetail("compareAdd")}
                </Button>
              </div>
            </article>
          );
        })}
      </div>

      <p className="mx-auto mt-8 max-w-md text-center text-sm text-gray-500 dark:text-gray-400">
        <Link href="/cars" className="font-semibold text-[#E63946] underline-offset-4 hover:underline">
          {tPick("pickBrowseFull")}
        </Link>
      </p>
    </section>
  );
}
